import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Download, FileText, Image as ImageIcon, Loader2, Sparkles, BrainCircuit } from "lucide-react";
import { api, authUrl, formatApiError, formatSize, formatDate } from "@/lib/api";

export default function BrandAssets({ brandId, kind, withCategories = false }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("");
  const [filter, setFilter] = useState("");
  const [aiContext, setAiContext] = useState("");
  const [aiUpdated, setAiUpdated] = useState(null);
  const [ctxBusy, setCtxBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/brands/${brandId}/assets`, { params: { kind } })
      .then(({ data }) => setAssets(data))
      .finally(() => setLoading(false));
  }, [brandId, kind]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (kind !== "documento") return;
    api.get(`/brands/${brandId}`).then(({ data }) => {
      setAiContext(data.ai_context || "");
      setAiUpdated(data.ai_context_updated_at || null);
    }).catch(() => {});
  }, [brandId, kind]);

  const buildContext = async () => {
    setCtxBusy(true);
    try {
      const { data } = await api.post(`/brands/${brandId}/ai-context`);
      setAiContext(data.ai_context);
      setAiUpdated(data.ai_context_updated_at);
      toast.success(`Contesto generato da ${data.files_read} documenti`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCtxBusy(false);
    }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("category", category);
        fd.append("file", file);
        await api.post(`/brands/${brandId}/assets`, fd);
      }
      toast.success("Caricamento completato");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Eliminare "${a.filename}"?`)) return;
    await api.delete(`/assets/${a.id}`);
    load();
  };

  const categories = [...new Set(assets.map((a) => a.category).filter(Boolean))];
  const visible = filter ? assets.filter((a) => a.category === filter) : assets;

  if (loading) return <div className="flex justify-center py-14"><Loader2 className="animate-spin text-stone2e" size={24} /></div>;

  return (
    <div data-testid={`assets-${kind}`}>
      <div className="flex flex-wrap items-end gap-4 mb-8">
        {withCategories && (
          <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Categoria (es. Icone, Prodotti)" data-testid="asset-category-input" className="field-line max-w-xs" />
        )}
        <label className={`inline-flex items-center gap-2 bg-black text-greige px-6 py-3 uppercase tracking-widest text-xs cursor-pointer hover:bg-stone2e transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="asset-upload-button">
          {uploading ? <Loader2 className="animate-spin" size={15} /> : <Upload strokeWidth={1.5} size={15} />}
          {uploading ? "Caricamento…" : "Carica file"}
          <input type="file" multiple className="hidden" onChange={(e) => upload([...e.target.files])} />
        </label>
      </div>

      {withCategories && categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => setFilter("")} data-testid="category-filter-all" className={`px-4 py-1.5 text-[0.65rem] uppercase tracking-widest border ${!filter ? "bg-black text-greige border-black" : "border-black/30 text-stone2e hover:border-black"}`}>Tutte</button>
          {categories.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`category-filter-${c}`} className={`px-4 py-1.5 text-[0.65rem] uppercase tracking-widest border ${filter === c ? "bg-black text-greige border-black" : "border-black/30 text-stone2e hover:border-black"}`}>{c}</button>
          ))}
        </div>
      )}

      {visible.length === 0 ? (
        <p className="font-editorial text-xl text-stone2e italic py-10 text-center">Nessun file caricato.</p>
      ) : kind === "libreria" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {visible.map((a) => (
            <div key={a.id} className="group bg-white/60 border border-white p-3" data-testid={`asset-${a.id}`}>
              <div className="h-28 bg-greige/70 flex items-center justify-center overflow-hidden mb-2">
                {a.content_type?.startsWith("image/") ? (
                  <img src={authUrl(`/assets/${a.id}/download`)} alt={a.filename} className="max-h-28 w-full object-cover" />
                ) : (
                  <ImageIcon size={26} strokeWidth={1.25} className="text-stone2e" />
                )}
              </div>
              <p className="font-body text-xs text-ink truncate">{a.filename}</p>
              <div className="flex items-center justify-between mt-1">
                <span className="meta-label">{(a.category || "—").toUpperCase()}</span>
                <button onClick={() => remove(a)} data-testid={`delete-asset-${a.id}`} className="text-stone2e hover:text-ink opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} strokeWidth={1.5} /></button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-px bg-black/10 border border-black/10">
          {visible.map((a) => (
            <div key={a.id} className="group bg-greige/60 hover:bg-white/70 transition-colors p-4 flex items-center justify-between gap-3" data-testid={`asset-${a.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={18} strokeWidth={1.5} className="text-stone2e shrink-0" />
                <div className="min-w-0">
                  <p className="font-body text-sm text-ink truncate">{a.filename}</p>
                  <p className="meta-label mt-0.5">{formatSize(a.size)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <a href={authUrl(`/assets/${a.id}/download`)} target="_blank" rel="noreferrer" data-testid={`download-asset-${a.id}`} className="text-stone2e hover:text-ink"><Download size={16} strokeWidth={1.5} /></a>
                <button onClick={() => remove(a)} data-testid={`delete-asset-${a.id}`} className="text-stone2e hover:text-ink"><Trash2 size={16} strokeWidth={1.5} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {kind === "documento" && (
        <div className="mt-10 border border-black/15 bg-white/50 p-6" data-testid="ai-context-panel">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <BrainCircuit size={18} strokeWidth={1.5} className="text-ink" />
              <h4 className="font-display text-xl text-ink">Contesto AI del brand</h4>
            </div>
            <button onClick={buildContext} disabled={ctxBusy} data-testid="build-ai-context-button"
              className="inline-flex items-center gap-2 bg-black text-greige px-5 py-2.5 text-[0.65rem] uppercase tracking-widest hover:bg-stone2e transition-colors disabled:opacity-60">
              {ctxBusy ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} strokeWidth={1.5} />}
              {ctxBusy ? "Lettura documenti…" : (aiContext ? "Rigenera contesto" : "Genera contesto AI")}
            </button>
          </div>
          <p className="font-body text-xs text-stone2e mb-4">
            L'AI legge i documenti caricati (PDF, TXT, CSV, immagini) e crea una scheda di contesto usata per generare i testi nel PED col tono di voce del brand.
          </p>
          {aiContext ? (
            <>
              {aiUpdated && <p className="meta-label mb-2">AGGIORNATO IL {formatDate(aiUpdated).toUpperCase()}</p>}
              <div className="bg-greige/70 border border-black/10 p-4 max-h-72 overflow-y-auto whitespace-pre-wrap font-body text-sm text-ink" data-testid="ai-context-text">
                {aiContext}
              </div>
            </>
          ) : (
            <p className="font-editorial italic text-stone2e">Nessun contesto generato. Carica i documenti del brand e premi «Genera contesto AI».</p>
          )}
        </div>
      )}
    </div>
  );
}
